import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AlarmsListResponse, AnnouncementPreviewResponse, AuditResponse, BindingsResponse, CallJourneyResponse, ChainResponse, ConcurrencyResponse, ContactAuditResponse, ContactRecordsResponse, ContactSearchResponse, DayWindow, EnvironmentDetailResponse, EnvironmentRole, EnvironmentSettingsInput, EnvironmentSettingsResponse, EnvironmentsListResponse, EventType, EventsListResponse, FocusResponse, GrantRoleResponse, InventoryResponse, InviteUserResponse, JourneyDimension, JourneyOverviewResponse, JourneyPathsResponse, MembershipsResponse, MyMembershipsResponse, OkResponse, OrganisationUsersResponse, OverridesResponse, PlatformSummaryResponse, PlatformTenantsResponse, PlatformUsersResponse, ProfileCreatedResponse, ProfileDetailResponse, ProfileField, ProfilesResponse, PromotionDecisionResponse, PromotionDiffResponse, PromotionResult, PromotionsResponse, PublishResult, QuotaIncreaseRequestInput, QuotaIncreaseRequestOutcome, QuotaIncreaseRequestsResponse, QuotasListResponse, RevertInput, RevertPreviewResponse, ReorderChainResponse, SetOverrideResponse, SupportCasesListResponse, UsageResponse, UsageRollupResponse, VersionsResponse, VoicesResponse } from "@starsach/shared-types";
import { useAuth } from "../auth/AuthProvider";
import { fetchBlob, fetchJson } from "./client";

/** TanStack Query owns server state (CLAUDE.md section 11); every hook goes through the one client. */
export function useApi() {
  const { config, session } = useAuth();
  if (!config || !session) throw new Error("API hooks require a signed-in session");
  return <T>(path: string, init?: RequestInit) => fetchJson<T>(config, session, path, init);
}

export function useCustomers(window: DayWindow, source: EventType = "cloudwatch_alarm") {
  const api = useApi();
  return useQuery({
    queryKey: ["customers", window, source],
    queryFn: () => api<EnvironmentsListResponse>(`/v1/environments?window=${window}&source=${source}`),
    staleTime: 30_000,
  });
}

export function useCustomer(environmentId: string) {
  const api = useApi();
  return useQuery({ queryKey: ["customer", environmentId], queryFn: () => api<EnvironmentDetailResponse>(`/v1/environments/${environmentId}`) });
}

export function useEvents(environmentId: string, source: EventType, window: DayWindow) {
  const api = useApi();
  return useQuery({
    queryKey: ["events", environmentId, source, window],
    queryFn: () => api<EventsListResponse>(`/v1/environments/${environmentId}/events?source=${source}&window=${window}&limit=100`),
  });
}

export function useAlarms(environmentId: string) {
  const api = useApi();
  return useQuery({ queryKey: ["alarms", environmentId], queryFn: () => api<AlarmsListResponse>(`/v1/environments/${environmentId}/alarms`) });
}

export function useQuotas(environmentId: string) {
  const api = useApi();
  return useQuery({ queryKey: ["quotas", environmentId], queryFn: () => api<QuotasListResponse>(`/v1/environments/${environmentId}/quotas`) });
}

export function useSupportCases(environmentId: string) {
  const api = useApi();
  return useQuery({ queryKey: ["support-cases", environmentId], queryFn: () => api<SupportCasesListResponse>(`/v1/environments/${environmentId}/support-cases`) });
}

export function useConcurrency(environmentId: string, hours: number) {
  const api = useApi();
  return useQuery({ queryKey: ["concurrency", environmentId, hours], queryFn: () => api<ConcurrencyResponse>(`/v1/environments/${environmentId}/concurrency?hours=${hours}`) });
}

export function useFocus() {
  const api = useApi();
  return useQuery({ queryKey: ["focus"], queryFn: () => api<FocusResponse>("/v1/focus"), refetchInterval: 60_000 });
}

export function useQuotaIncreaseRequests(environmentId: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ["quota-increase-requests", environmentId],
    queryFn: () => api<QuotaIncreaseRequestsResponse>(`/v1/environments/${environmentId}/quota-increase-requests`),
    enabled,
  });
}

/** Asks central to look at one request with AWS now, whatever its age; the list refetches with the answer. */
export function useCheckQuotaIncreaseRequest(environmentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (awsRequestId: string) =>
      api<QuotaIncreaseRequestsResponse>(`/v1/environments/${environmentId}/quota-increase-requests?check=${encodeURIComponent(awsRequestId)}`),
    onSuccess: (data) => {
      qc.setQueryData(["quota-increase-requests", environmentId], data);
      void qc.invalidateQueries({ queryKey: ["quotas", environmentId] });
    },
  });
}

export function useRequestQuotaIncrease(environmentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: QuotaIncreaseRequestInput) =>
      api<QuotaIncreaseRequestOutcome>(`/v1/environments/${environmentId}/quota-increase-requests`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quota-increase-requests", environmentId] });
      void qc.invalidateQueries({ queryKey: ["quotas", environmentId] });
    },
  });
}

export function useJourneyPaths(environmentId: string, dimension: JourneyDimension, slice: string, days: number) {
  const api = useApi();
  return useQuery({
    queryKey: ["journey-paths", environmentId, dimension, slice, days],
    queryFn: () => api<JourneyPathsResponse>(`/v1/environments/${environmentId}/journeys/paths?dimension=${dimension}&slice=${encodeURIComponent(slice)}&days=${days}`),
  });
}

/** Requirements 8.4: the overview scorecard, day buckets - one small read per window length. */
export function useJourneyOverview(environmentId: string, days: 7 | 30 | 90) {
  const api = useApi();
  return useQuery({ queryKey: ["journey-overview", environmentId, days], queryFn: () => api<JourneyOverviewResponse>(`/v1/environments/${environmentId}/journeys/overview?days=${days}`), staleTime: 60_000 });
}

/** The Contacts list from contact records (ADR-0015): labels and timings, never content. */
export function useContactRecords(environmentId: string, params: { from?: string; to?: string; channel?: string; outcome?: string; limit?: number }) {
  const api = useApi();
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== "").map(([k, v]) => [k, String(v)]));
  return useQuery({ queryKey: ["contact-records", environmentId, qs.toString()], queryFn: () => api<ContactRecordsResponse>(`/v1/environments/${environmentId}/journeys/records?${qs.toString()}`), staleTime: 30_000 });
}

export function useContactSearch(environmentId: string, params: { from: string; to: string; ani: string; dnis: string } | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["contact-search", environmentId, params],
    // `to` is a day the person picked, so the search runs to the end of it.
    queryFn: () => api<ContactSearchResponse>(`/v1/environments/${environmentId}/journeys/contacts?from=${params!.from}&to=${encodeURIComponent(`${params!.to}T23:59:59Z`)}&ani=${encodeURIComponent(params!.ani)}&dnis=${encodeURIComponent(params!.dnis)}`),
    enabled: params !== null,
  });
}

/** Polls while the assembler works; the API returns a partial view that fills in (design.md "Journeys module"). */
export function useContactAudit(environmentId: string, contactId: string) {
  const api = useApi();
  return useQuery({
    queryKey: ["contact-audit", environmentId, contactId],
    queryFn: () => api<ContactAuditResponse>(`/v1/environments/${environmentId}/journeys/contacts/${contactId}/audit`),
  });
}

export function useCallJourney(environmentId: string, contactId: string) {
  const api = useApi();
  return useQuery({
    queryKey: ["call-journey", environmentId, contactId],
    queryFn: () => api<CallJourneyResponse>(`/v1/environments/${environmentId}/journeys/contacts/${contactId}`),
    refetchInterval: (q) => (q.state.data?.status === "assembling" || !q.state.data ? 2000 : false),
  });
}

/* --- Flow configuration (requirements.md section 10) --- */

export function useProfiles(environmentId: string) {
  const api = useApi();
  return useQuery({ queryKey: ["profiles", environmentId], queryFn: () => api<ProfilesResponse>(`/v1/environments/${environmentId}/profiles`) });
}

export function useProfile(environmentId: string, profileId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ["profile", environmentId, profileId],
    queryFn: () => api<ProfileDetailResponse>(`/v1/environments/${environmentId}/profiles/${profileId}`),
    enabled: Boolean(profileId),
  });
}

export function useInventory(environmentId: string) {
  const api = useApi();
  return useQuery({
    queryKey: ["inventory", environmentId],
    queryFn: () => api<InventoryResponse>(`/v1/environments/${environmentId}/inventory`),
    staleTime: 5 * 60_000,
  });
}

/** Which numbers reach which profile in this environment (requirements.md 10.2). */
export function useBindings(environmentId: string) {
  const api = useApi();
  return useQuery({
    queryKey: ["bindings", environmentId],
    queryFn: () => api<BindingsResponse>(`/v1/environments/${environmentId}/bindings`),
  });
}

export function useBind(environmentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { alias: string; profileId: string }) =>
      api<OkResponse>(`/v1/environments/${environmentId}/bindings`, { method: "POST", body: JSON.stringify({ ...body, manual: true }) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["bindings", environmentId] });
      invalidateConfig(qc, environmentId);
    },
  });
}

export function useUnbind(environmentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { alias: string }) => api<OkResponse>(`/v1/environments/${environmentId}/bindings/remove`, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["bindings", environmentId] });
      invalidateConfig(qc, environmentId);
    },
  });
}

/** ADR-0013: the organisation's people. */
export function useOrganisationUsers() {
  const api = useApi();
  return useQuery({ queryKey: ["organisation-users"], queryFn: () => api<OrganisationUsersResponse>("/v1/organisations/me/users") });
}
export function useInviteUser() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string }) => api<InviteUserResponse>("/v1/organisations/me/users", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["organisation-users"] }),
  });
}
export function useUserLifecycle() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, action }: { userId: string; action: "disable" | "enable" | "remove" }) => api<OkResponse>(`/v1/organisations/me/users/${encodeURIComponent(userId)}/${action}`, { method: "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["organisation-users"] }),
  });
}
export function useLeaveOrganisation() {
  const api = useApi();
  return useMutation({ mutationFn: () => api<OkResponse>("/v1/me/leave", { method: "POST" }) });
}

export function useConfigVersions(environmentId: string, profileId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ["config-versions", environmentId, profileId],
    queryFn: () => api<VersionsResponse>(`/v1/environments/${environmentId}/profiles/${profileId}/versions`),
    enabled: Boolean(profileId),
  });
}

/** A profile is authored once for the organisation (requirements.md section 10.2); this is where one begins. */
export function useCreateProfile(environmentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string }) =>
      api<ProfileCreatedResponse>(`/v1/environments/${environmentId}/profiles`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(input) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["profiles", environmentId] }),
  });
}

export function usePublishProfile(environmentId: string, profileId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { fields: ProfileField[]; breakGlass?: boolean }) =>
      api<PublishResult>(`/v1/environments/${environmentId}/profiles/${profileId}`, { method: "PUT", body: JSON.stringify(body) }),
    onSuccess: () => invalidateConfig(qc, environmentId),
  });
}

export function useRollback(environmentId: string, profileId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { version: number }) =>
      api<PublishResult>(`/v1/environments/${environmentId}/profiles/${profileId}/rollback`, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => invalidateConfig(qc, environmentId),
  });
}

/** Every active override in the environment, across profiles - the Profiles list reads it for the emergency-close mark. */
export function useOverrides(environmentId: string) {
  const api = useApi();
  return useQuery({ queryKey: ["overrides", environmentId], queryFn: () => api<OverridesResponse>(`/v1/environments/${environmentId}/overrides`), staleTime: 15_000 });
}

/** Hear an announcement as a caller would: text or SSML through Polly in a chosen voice. Nothing is stored. */
export function useAnnouncementPreview(environmentId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: (body: { text: string; ssml: boolean; voice: string }) =>
      api<AnnouncementPreviewResponse>(`/v1/environments/${environmentId}/announcements/preview`, { method: "POST", body: JSON.stringify(body) }),
  });
}

/** The voices an announcement can be heard in - Polly's list for the region, which is what Connect's Play prompt block offers too. */
export function useVoices(environmentId: string) {
  const api = useApi();
  return useQuery({
    queryKey: ["voices", environmentId],
    queryFn: () => api<VoicesResponse>(`/v1/environments/${environmentId}/announcements/voices`),
    staleTime: 60 * 60_000,
  });
}

export function useSetOverride(environmentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { profileId: string; field: string; value: unknown; reason: string; startsAt?: string; expiresAt?: string }) =>
      api<SetOverrideResponse>(`/v1/environments/${environmentId}/overrides`, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => invalidateConfig(qc, environmentId),
  });
}

export function useClearOverride(environmentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { profileId: string; field: string }) =>
      api<OkResponse>(`/v1/environments/${environmentId}/overrides/clear`, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => invalidateConfig(qc, environmentId),
  });
}

export function usePromotionPreview(environmentId: string, profileId: string | undefined, sourceEnvironmentId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ["promotion-preview", environmentId, profileId, sourceEnvironmentId],
    queryFn: () =>
      api<PromotionDiffResponse>(`/v1/environments/${environmentId}/promotions/preview`, {
        method: "POST",
        body: JSON.stringify({ profileId, sourceEnvironmentId }),
      }),
    enabled: Boolean(profileId && sourceEnvironmentId),
  });
}

export function usePromote(environmentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { profileId: string; sourceEnvironmentId: string; decisions: Array<{ field: string; choice: "take_source" | "keep_target" }>; targetInventoryVersion?: number }) =>
      api<PromotionResult>(`/v1/environments/${environmentId}/promotions`, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => invalidateConfig(qc, environmentId),
  });
}

/** Every change request into this environment - pending, applied, rejected - newest first. */
export function usePromotions(environmentId: string) {
  const api = useApi();
  return useQuery({ queryKey: ["promotions", environmentId], queryFn: () => api<PromotionsResponse>(`/v1/environments/${environmentId}/promotions`) });
}

export function useDecidePromotion(environmentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ promotionId, approve }: { promotionId: string; approve: boolean }) =>
      api<PromotionDecisionResponse>(`/v1/environments/${environmentId}/promotions/${promotionId}/decision`, { method: "POST", body: JSON.stringify({ approve }) }),
    onSuccess: () => {
      invalidateConfig(qc, environmentId);
      void qc.invalidateQueries({ queryKey: ["promotions", environmentId] });
    },
  });
}

function invalidateConfig(qc: ReturnType<typeof useQueryClient>, environmentId: string) {
  for (const key of ["profile", "profiles", "config-versions", "promotion-preview", "overrides"]) {
    void qc.invalidateQueries({ queryKey: [key, environmentId] });
  }
}

/* --- Platform view (requirements.md section 12) --- */

/** #272: what this environment's stack metered, the last three months. */
export function useUsage(environmentId: string) {
  const api = useApi();
  return useQuery({
    queryKey: ["usage", environmentId],
    queryFn: () => api<UsageResponse>(`/v1/environments/${environmentId}/usage`),
    staleTime: 60_000,
  });
}

/** #272: the invoice run, internal only - every organisation's month, priced. */
export function usePlatformUsage(month: string) {
  const api = useApi();
  return useQuery({
    queryKey: ["platform-usage", month],
    queryFn: () => api<UsageRollupResponse>(`/v1/platform/usage?month=${month}`),
    staleTime: 60_000,
  });
}

/** A file the API builds (a CSV), fetched with the token and handed to the browser. A mutation like any other action, so it is pending and failing on screen (#284). */
export function useDownload() {
  const { config, session } = useAuth();
  if (!config || !session) throw new Error("API hooks require a signed-in session");
  return useMutation({
    mutationFn: async (path: string) => {
      const { blob, filename } = await fetchBlob(config, session, path);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

export function usePlatformSummary(window: DayWindow) {
  const api = useApi();
  return useQuery({
    queryKey: ["platform-summary", window],
    queryFn: () => api<PlatformSummaryResponse>(`/v1/platform/summary?window=${window}`),
    staleTime: 60_000,
  });
}

export function usePlatformTenants(window: DayWindow) {
  const api = useApi();
  return useQuery({
    queryKey: ["platform-tenants", window],
    queryFn: () => api<PlatformTenantsResponse>(`/v1/platform/tenants?window=${window}`),
    staleTime: 60_000,
  });
}

export function usePlatformUsers(window: DayWindow) {
  const api = useApi();
  return useQuery({
    queryKey: ["platform-users", window],
    queryFn: () => api<PlatformUsersResponse>(`/v1/platform/users?window=${window}`),
    staleTime: 60_000,
  });
}

/* --- Req 30 (#67): where the caller holds access --- */

/**
 * Per-environment roles never ride the token, so this is the only way
 * the UI can tell a user where they hold access. Read-only.
 */
export function useMyMemberships() {
  const api = useApi();
  return useQuery({
    queryKey: ["my-memberships"],
    queryFn: () => api<MyMembershipsResponse>("/v1/me/access"),
    staleTime: 60_000,
  });
}

/* --- Access: roles, the chain, environment settings (ADR-0008) --- */

export function useMemberships(environmentId: string) {
  const api = useApi();
  return useQuery({ queryKey: ["memberships", environmentId], queryFn: () => api<MembershipsResponse>(`/v1/environments/${environmentId}/memberships`) });
}

export function useChain(environmentId: string) {
  const api = useApi();
  return useQuery({ queryKey: ["chain", environmentId], queryFn: () => api<ChainResponse>(`/v1/environments/${environmentId}/chain`) });
}

export function useGrantRole(environmentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { userId: string; role: EnvironmentRole }) =>
      api<GrantRoleResponse>(`/v1/environments/${environmentId}/memberships`, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => invalidateAccess(qc, environmentId),
  });
}

export function useRevokeRole(environmentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { userId: string }) =>
      api<OkResponse>(`/v1/environments/${environmentId}/memberships/revoke`, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => invalidateAccess(qc, environmentId),
  });
}

export function useReorderChain(environmentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { environmentIds: string[] }) =>
      api<ReorderChainResponse>(`/v1/environments/${environmentId}/chain`, { method: "PUT", body: JSON.stringify(body) }),
    onSuccess: () => invalidateAccess(qc, environmentId),
  });
}

export function useEnvironmentSettings(environmentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EnvironmentSettingsInput) =>
      api<EnvironmentSettingsResponse>(`/v1/environments/${environmentId}/settings`, { method: "PUT", body: JSON.stringify(body) }),
    onSuccess: () => invalidateAccess(qc, environmentId),
  });
}

function invalidateAccess(qc: ReturnType<typeof useQueryClient>, environmentId: string) {
  for (const key of ["memberships", "chain", "customer", "profile"]) void qc.invalidateQueries({ queryKey: [key, environmentId] });
}

export function useAudit(environmentId: string, scope: "environment" | "organisation") {
  const api = useApi();
  return useQuery({
    queryKey: ["audit", environmentId, scope],
    queryFn: () => api<AuditResponse>(`/v1/environments/${environmentId}/audit?scope=${scope}`),
  });
}

/** 11.6: what a revert of an audit entry would restore, field by field, with the current value where a field has moved since. */
export function useRevertPreview(environmentId: string, profileId: string | undefined, entryId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ["revert-preview", environmentId, profileId, entryId],
    queryFn: () => api<RevertPreviewResponse>(`/v1/environments/${environmentId}/profiles/${profileId}/revert-preview?entry=${encodeURIComponent(entryId ?? "")}`),
    enabled: Boolean(profileId && entryId),
  });
}

/** 11.6: the revert - a save of the entry's before values through the same gate as any change. */
export function useRevert(environmentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { profileId: string } & RevertInput) =>
      api<PublishResult>(`/v1/environments/${environmentId}/profiles/${body.profileId}/revert`, { method: "POST", body: JSON.stringify({ entryId: body.entryId, ...(body.fields ? { fields: body.fields } : {}) }) }),
    onSuccess: (_r, body) => {
      void qc.invalidateQueries({ queryKey: ["audit", environmentId] });
      void qc.invalidateQueries({ queryKey: ["revert-preview", environmentId] });
      void qc.invalidateQueries({ queryKey: ["profile", environmentId, body.profileId] });
      invalidateConfig(qc, environmentId);
    },
  });
}

export function useSetAlias(environmentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { type: string; name: string; resolvesTo: string }) =>
      api<OkResponse>(`/v1/environments/${environmentId}/aliases`, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["inventory", environmentId] });
      invalidateConfig(qc, environmentId);
    },
  });
}
